import { Fragment, type MouseEvent, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';

import { getAccountPlan } from '@/data/account/selectors';
import { shouldBeUpsold } from '@/data/account/selectors/shouldBeUpsold';
import { useLDFlag } from '@/data/LD/selectors/getLDFlag';
import { fetchPunchState } from '@/data/punchState/actions';
import useDialog from '@/dialogs/hooks/useDialog';
import { canViewDocStorage as getCanViewDocStorage } from '@/documents/selectors/canViewDocStorage';
import Notifications from '@/notifications';
import { CREATE_REQUEST_DIALOG, EDIT_REQUEST_DIALOG } from '@/requests/modules/timeoff/dialogs';
import RipplingNavCrossSell from '@/rippling/components/RipplingNavCrossSell';
import { getAuthAccount, getAuthUser } from '@/shared/auth/selectors';
import BulletinBoard from '@/shared/components/nav/BulletinBoard';
import { AccountNotifications } from '@/shared/components/nav/BulletinBoard/AccountNotifications';
import { MenuItem } from '@/shared/components/nav/MenuItem';
import MenuItems from '@/shared/components/nav/MenuItems';
import AppSettingsMenuItem from '@/shared/components/nav/menu-items/AppSettingsMenuItem';
import AttendanceMenuItem from '@/shared/components/nav/menu-items/AttendanceMenu';
import AvatarMenuItem from '@/shared/components/nav/menu-items/AvatarMenuItem';
import EmployeeMenuItem from '@/shared/components/nav/menu-items/EmployeeMenuItem';
import ReportsMenuItem from '@/shared/components/nav/menu-items/ReportsMenuItem';
import RequestsMenuItem from '@/shared/components/nav/menu-items/RequestsMenuItem';
import SchedulingMenuItem from '@/shared/components/nav/menu-items/SchedulingMenuItem';
import WorkchatMenuItem from '@/shared/components/nav/menu-items/WorkchatMenuItem';
import WorkplaceMenuItem from '@/shared/components/nav/menu-items/WorkplaceMenuItem';
import Features from '@/shared/features';
import useGetInstalledRelatedApps from '@/shared/hooks/useGetInstalledRelatedApps';
import { MULTIPLE_SCHEDULES_UPGRADE_DIALOG } from '@/shared/upsell/actions/dialogs';
import { isWebview } from '@/shared/util/isWebview';
import Mercury, { DOCS_INTERACTION } from '@/shared/util/mercury';
import { posthogCapture } from '@/shared/vendor/posthog';
import { useWiwDispatch, useWiwSelector } from '@/store';
import { openSendMessageDialog } from '@/teamtxt/dialogs';
import DownloadApp from './menu-items/DownloadApp';

import FocusTrap from 'focus-trap-react';
import { useMediaQuery } from 'usehooks-ts';

import '@/shared/components/nav/assets/styles/MainNav.scss';

export function MainNav() {
  const location = useLocation();
  const dispatch = useWiwDispatch();
  const { openDialog } = useDialog();

  const authAccount = useWiwSelector(getAuthAccount);
  const accountPlan = useWiwSelector(getAccountPlan);
  const authUser = useWiwSelector(getAuthUser);
  const shouldOpenShiftRequestsBeUpsold = useWiwSelector(state => shouldBeUpsold(state, Features.OPENSHIFT_APPROVAL));
  const canViewDocStorage = useWiwSelector(getCanViewDocStorage);

  const pt121WorkplaceObjects = useLDFlag('pt-121-finding-workplace-objects');
  const at383EditingTimeoff = useLDFlag('at-383-requests-editing');
  const TIME_OFF_DIALOG = at383EditingTimeoff ? EDIT_REQUEST_DIALOG : CREATE_REQUEST_DIALOG;

  const [mobileNavigation, setMobileNavigation] = useState(false);
  const useLargerBreakpoint = authUser.isAdmin();
  const isMdBreakpoint = useMediaQuery('(min-width: 768px)');
  const isLgBreakpoint = useMediaQuery('(min-width: 992px)');
  const isDesktop = useLargerBreakpoint ? isLgBreakpoint : isMdBreakpoint;
  const toggleMobileMenu = () => setMobileNavigation(!mobileNavigation);
  const naviconId = 'navicon';
  const hasAppInstalled = useGetInstalledRelatedApps();
  const isAndroid = /Android/i.test(navigator.userAgent);
  const shouldShowPlayStoreAd = !isDesktop && isAndroid && !hasAppInstalled;
  const hideWCFreePlan = (authUser.isManager() || authUser.isSupervisor()) && authAccount.isReverseTrial();

  function childClickedCloseMenu(event: MouseEvent) {
    const target = event.target as HTMLAnchorElement;
    !isDesktop && typeof target.href !== 'undefined' && setMobileNavigation(false);
    //if user clicks the span or icon inside the anchor
    !isDesktop && target.closest('a') && setMobileNavigation(false);
  }

  const punchStateLoading = useWiwSelector(state => state.data.punchState.loading);

  // mount effects
  useEffect(() => {
    if (!punchStateLoading && authAccount.hasAttendance() && authAccount.getSettings('clockin.work.enabled')) {
      dispatch(fetchPunchState({}));
    }
  }, [dispatch, authAccount]);

  useEffect(() => {
    if (location.hash === '#request-timeoff') {
      openDialog(TIME_OFF_DIALOG);
      return;
    }
  }, [dispatch, location.hash]);

  useEffect(() => {
    if (location.hash === '#send-message') {
      dispatch(openSendMessageDialog());
    }
  }, [dispatch, location.hash]);

  function upsellMultipleSchedules(event: Event) {
    event.preventDefault();
    openDialog(MULTIPLE_SCHEDULES_UPGRADE_DIALOG);
  }

  function trackDocumentsNav() {
    Mercury.track(
      DOCS_INTERACTION,
      {
        action: 'documentNavClicked',
        documentId: null,
        hash: location.hash,
        // the react-router Location object doesn't have hostname :(
        hostName: window.location.hostname,
        path: location.pathname,
        queryParams: location.search,
        annotations: null,
      },
      {},
    );
  }

  function renderSchedulingMenu() {
    const hideMenu = !authUser.canSupervise() && !authAccount.hasScheduling();

    if (hideMenu) {
      return null;
    }

    if (authAccount.isWorkchatFree()) {
      return <SchedulingMenuItem workChatFree />;
    }

    if (!authAccount.hasScheduling()) {
      return <SchedulingMenuItem schedulerTrial />;
    }

    if (!authUser.canSupervise()) {
      const showTeamSchedule =
        authAccount.getSettings('schedule.is_visible', true) ||
        authAccount.getSettings('schedule.positions_only', false);

      return (
        <Fragment>
          <SchedulingMenuItem isEmployee />
          <SchedulingMenuItem showTeamSchedule={showTeamSchedule} />
        </Fragment>
      );
    }

    return <SchedulingMenuItem defaultMenuItem />;
  }

  function renderAttendanceMenu() {
    return <AttendanceMenuItem />;
  }

  function renderMessagingMenu() {
    // Hidden for all reverse trial accounts
    if (hideWCFreePlan) {
      return null;
    }

    // If WorkChat has been enabled, show it
    if (authAccount.getSettings('workchat.enabled', false)) {
      return <WorkchatMenuItem isDesktop={isDesktop} workchat />;
    }

    // If the account hasn't turned on WorkChat before, show the setup
    if (!authAccount.getSettings('workchat.toggled') && authUser.canManage()) {
      return <WorkchatMenuItem isDesktop={isDesktop} setup />;
    }

    if (authUser.canSupervise()) {
      return <WorkchatMenuItem isDesktop={isDesktop} broadcast />;
    }
  }

  function renderReportsButton() {
    if (!authUser.canManage()) {
      return null;
    }

    return <ReportsMenuItem />;
  }

  function renderBulletinBoard() {
    return (
      <BulletinBoard authUser={authUser} authAccount={authAccount} accountPlan={accountPlan} isDesktop={isDesktop} />
    );
  }

  function renderRequestsMenu() {
    const shouldHideMenu =
      authAccount.isWorkchatFree() ||
      (!authUser.canSupervise() &&
        !authAccount.getSettings('timeoff.enabled') &&
        !authAccount.getSettings('swaps.enabled') &&
        !authAccount.hasFeature(Features.OPENSHIFT_APPROVAL, true) &&
        !shouldOpenShiftRequestsBeUpsold);

    if (shouldHideMenu) {
      return null;
    }

    return (
      <RequestsMenuItem
        authUser={authUser}
        authAccount={authAccount}
        featureOpenShiftApproval={Features.OPENSHIFT_APPROVAL}
        requestsEnabled={authAccount.getSettings('timeoff.enabled')}
        schedulerEnabled={authAccount.hasScheduling()}
        swapsEnabled={authAccount.getSettings('swaps.enabled')}
      />
    );
  }

  function renderWorkplaceMenu() {
    if (authUser.canSupervise()) {
      return (
        <WorkplaceMenuItem
          authUser={authUser}
          authAccount={authAccount}
          accountPlan={accountPlan}
          trackDocumentsNav={trackDocumentsNav}
          pt121WorkplaceObjects={pt121WorkplaceObjects}
          upsellMultipleSchedules={upsellMultipleSchedules}
          canViewDocStorage={canViewDocStorage}
        />
      );
    }
  }

  function renderAppSettingsMenu() {
    const canManageSettings =
      authUser.isAdmin() ||
      (authUser.isManager() && authAccount.getSettings('role_permissions.managers.can_manage_account_settings', true));

    if (canManageSettings) {
      return <AppSettingsMenuItem authUser={authUser} authAccount={authAccount} accountPlan={accountPlan} />;
    }
  }

  function renderEmployeeMenu() {
    if (!authUser.canSupervise()) {
      return <EmployeeMenuItem trackDocumentsNav={trackDocumentsNav} canViewDocStorage={canViewDocStorage} />;
    }
  }

  function renderAvatarMenu() {
    return <AvatarMenuItem authUser={authUser} authAccount={authAccount} />;
  }

  function renderDownloadApp() {
    return <DownloadApp />;
  }

  function renderRipplingCrossSell() {
    const isViewingAttendance = /^\/attendance|timesheets(\/)?$/i.test(location.pathname);
    const hasAttendanceCountdown = isViewingAttendance && authAccount.isAttendanceTrial();
    const hasTrialCountdown = accountPlan.isTrial() || hasAttendanceCountdown;

    // don't show both trial countdown and cross-sell on small viewports
    if (hasTrialCountdown && !isDesktop) {
      return null;
    }

    if (!authUser.canManage()) {
      return null;
    }

    return <RipplingNavCrossSell isDesktop={isDesktop} />;
  }

  // hide the nav if we're onboarding scheduler or user is on a webview
  if (location.pathname.match(/^\/scheduler\/onboarding(\/)?$/i) || isWebview()) {
    return null;
  }

  const menuText = hasAppInstalled ? 'App' : 'Menu';
  return (
    <nav
      id="main-header"
      className={`main-navigation navbar d-print-none ${useLargerBreakpoint ? 'navbar-expand-lg' : 'navbar-expand-md'}`}
    >
      <div className="message-group">
        {!authAccount.isWorkchatFree() && <Notifications container="main-header" />}
        {!isDesktop && renderMessagingMenu()}
      </div>

      {!isDesktop && (
        <MenuItems className="navbar-account-notifications">
          <AccountNotifications authUser={authUser} authAccount={authAccount} accountPlan={accountPlan} />
        </MenuItems>
      )}

      {!isDesktop && renderRipplingCrossSell()}
      <button
        className="navbar-toggler"
        type="button"
        data-toggle="collapse"
        data-target="#navbarSupportedContent"
        aria-controls="navbarSupportedContent"
        aria-expanded={mobileNavigation}
        aria-label="Toggle navigation"
        id={naviconId}
        onClick={toggleMobileMenu}
      >
        <i className="wiw-icon wiw-more-horizontal-lines" /> {menuText}
      </button>

      <FocusTrap
        active={mobileNavigation}
        focusTrapOptions={{
          onDeactivate: () => setMobileNavigation(false),
          allowOutsideClick: true,
          initialFocus: () => !isDesktop && (document.querySelector('.navbar-nav') as any),
          clickOutsideDeactivates: event => (event.target as Element).id !== naviconId,
        }}
      >
        {/** biome-ignore lint/a11y/noStaticElementInteractions: required */}
        <div
          className={`collapse navbar-collapse ${mobileNavigation ? 'show' : ''}`}
          id="navbarSupportedContent"
          onClick={(event: MouseEvent) => childClickedCloseMenu(event)}
        >
          <MenuItems className="navbar-nav" countChildren>
            {!isDesktop && renderBulletinBoard()}
            {authAccount.hasScheduling() && authUser.canSupervise() && (
              <MenuItem
                data-testid="top-nav-dashboard"
                icon="dashboard"
                exact
                hideLabel="tablet"
                label="Dashboard"
                to="/"
                onClick={() => posthogCapture('top_nav.dashboard')}
              />
            )}

            {renderSchedulingMenu()}
            {renderAttendanceMenu()}
            {isDesktop && renderMessagingMenu()}
            {renderReportsButton()}
            {isDesktop && renderBulletinBoard()}
            {isDesktop && renderRipplingCrossSell()}
            {renderRequestsMenu()}
            {renderWorkplaceMenu()}
            {renderAppSettingsMenu()}
            {renderEmployeeMenu()}
            {renderAvatarMenu()}
            {shouldShowPlayStoreAd && renderDownloadApp()}
          </MenuItems>
        </div>
      </FocusTrap>
    </nav>
  );
}
